import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Leadership() {
  const [selectedClub, setSelectedClub] = useState<number | null>(null);
  const [selectedCommittee, setSelectedCommittee] = useState<number | null>(null);
  const navigate = useNavigate();

  // Lock body scroll when any modal is open
  useEffect(() => {
    const isOpen = selectedClub !== null || selectedCommittee !== null;
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [selectedClub, selectedCommittee]);

  const committees = [
    { 
      name: '公關委員會', 
      chair: '游博硯',
      icon: 'ri-megaphone-line',
      description: '負責社團對外宣傳與曝光，提升台北青企社的品牌形象與社會影響力。',
      responsibilities: ['媒體關係維護與新聞稿發布', '社群媒體經營與內容策劃', '活動宣傳與品牌形象推廣', '對外合作洽談與公關活動']
    },
    { 
      name: '社務發展委員會', 
      chair: '陳宥亘',
      icon: 'ri-settings-3-line',
      description: '統籌社團內部事務運作，確保各項活動順利進行，推動社團永續發展。',
      responsibilities: ['社團規章制度制定與修訂', '會員服務與權益維護', '內部行政事務協調', '社團發展策略規劃']
    },
    { 
      name: '企業發展委員會', 
      chair: '薛英林',
      icon: 'ri-briefcase-line',
      description: '促進會員企業間的商業合作與資源整合，創造互利共贏的商業機會。',
      responsibilities: ['企業參訪與產業交流', '商業媒合與合作促進', '產業趨勢研究與分享', '創業輔導與資源對接']
    },
    { 
      name: '聯誼委員會', 
      chair: '蘇育民',
      icon: 'ri-group-line',
      description: '規劃各類聯誼活動，增進會員間的情誼交流，營造溫馨的社團氛圍。',
      responsibilities: ['會員聯誼活動策劃', '家庭日與親子活動', '節慶聚會與慶祝活動', '新會員迎新與融入']
    },
    { 
      name: '會刊編輯委員會', 
      chair: '湯宗霖',
      icon: 'ri-newspaper-line',
      description: '負責社刊編輯與出版，記錄社團活動點滴，傳承青企精神與歷史。',
      responsibilities: ['社刊內容企劃與編輯', '活動紀錄與攝影', '會員專訪與故事撰寫', '歷史資料整理與保存']
    }
  ];

  const subClubs = [
    { 
      name: '投資研究社', 
      icon: 'ri-line-chart-line',
      description: '專注於投資理財研究，定期分享市場趨勢分析、投資策略與財務規劃經驗。',
      activities: ['每月投資論壇', '產業研究報告', '投資實戰分享']
    },
    { 
      name: '讀書會', 
      icon: 'ri-book-open-line',
      description: '透過閱讀與討論，提升會員的知識視野與思維深度，涵蓋商業管理、人文社科等領域。',
      activities: ['每月選書討論', '作者座談會', '讀書心得分享']
    },
    { 
      name: '國際交流社', 
      icon: 'ri-global-line',
      description: '促進國際青商組織交流，拓展會員國際視野，建立跨國商業合作機會。',
      activities: ['國際參訪團', '外賓接待', '跨國合作專案']
    },
    { 
      name: '登山社', 
      icon: 'ri-compass-3-line',
      description: '結合戶外運動與團隊建立，透過登山活動培養堅毅精神與團隊默契。',
      activities: ['週末登山活動', '百岳挑戰', '戶外領導訓練']
    },
    { 
      name: '高爾夫社', 
      icon: 'ri-golf-ball-line',
      description: '提供會員高爾夫球技交流平台，在運動中建立深厚友誼與商業網絡。',
      activities: ['月例賽', '球技教學', '企業聯誼賽']
    },
    { 
      name: 'EMBA社', 
      icon: 'ri-graduation-cap-line',
      description: '匯聚 EMBA 校友，分享管理實務經驗，促進學術與實務的深度結合。',
      activities: ['管理講座', '案例研討', '校友聯誼']
    },
    { 
      name: '桌球社', 
      icon: 'ri-ping-pong-line',
      description: '透過桌球運動促進會員身心健康，在輕鬆氛圍中增進彼此情誼。',
      activities: ['週三球敘', '桌球錦標賽', '球技訓練班']
    },
    { 
      name: '參議會', 
      icon: 'ri-medal-line',
      description: '由歷屆社長與資深會員組成，傳承青企精神，提供新世代會員寶貴經驗與指導。',
      activities: ['經驗傳承座談', '顧問諮詢', '重大決策建議']
    }
  ];

  return (
    <section id="leadership" className="py-16 sm:py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* President Section */}
        <div className="mb-12 sm:mb-20">
          <p className="text-xs sm:text-sm font-light text-gray-500 mb-2 sm:mb-3 tracking-wider uppercase text-center sm:text-left">Leadership</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-purple-900 mb-8 sm:mb-12 text-center sm:text-left">
            組織架構與領導團隊
          </h2>

          <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 shadow-lg">
            <div className="flex flex-col lg:grid lg:grid-cols-5 gap-8 lg:gap-12 items-center">
              {/* President Photo */}
              <div className="lg:col-span-2 w-full max-w-xs sm:max-w-sm lg:max-w-none mx-auto">
                <div className="relative">
                  <img 
                    src="https://static.readdy.ai/image/5ea2d03b33219ef9494c85fcc936fae1/f76bc3c48fa56201ed549cce865a84f7.jpeg"
                    alt="許政賓 社長"
                    className="w-full h-auto rounded-xl sm:rounded-2xl shadow-xl ring-4 ring-purple-600/20 object-cover"
                  />
                </div>
                <div className="mt-4 sm:mt-6 text-center">
                  <h3 className="text-2xl sm:text-3xl font-bold text-purple-900">許政賓</h3>
                  <p className="text-orange-600 font-light tracking-wide mt-1 sm:mt-2 text-sm sm:text-base">2026 President</p>
                </div>
              </div>

              {/* President Message */}
              <div className="lg:col-span-3 space-y-4 sm:space-y-6">
                <div className="relative pl-4 sm:pl-6 border-l-4 border-purple-600">
                  <p className="text-gray-700 leading-relaxed text-base sm:text-lg">
                    「在這個快速變遷的時代，青年企業家需要的不僅是商業知識，更需要建立深厚的人際網絡與社會責任感。台北青企社致力於打造一個讓年輕企業家共同成長、互相扶持的平台。」
                  </p>
                </div>
                <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                  2026 年，我們將持續深化三大核心價值：透過專業研究提升企業競爭力、透過真摯友誼建立長久合作關係、透過社會服務實踐企業家精神。期待與各位會員攜手，共創台北青企社更輝煌的未來。
                </p>
                <div className="pt-2 sm:pt-4">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/9e098cd49e3b708df6b5cb272b9d1b79.jpg"
                    alt="社長簽名"
                    className="h-12 sm:h-16 w-auto opacity-70 mx-auto lg:mx-0"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Committees and Sub-Clubs */}
        <div className="grid lg:grid-cols-2 gap-8 sm:gap-12">
          {/* Committees */}
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-purple-900 mb-6 sm:mb-8 text-center sm:text-left">委員會</h3>
            <div className="space-y-3 sm:space-y-4">
              {committees.map((committee, index) => (
                <div 
                  key={index}
                  onClick={() => setSelectedCommittee(index)}
                  className="bg-purple-50 rounded-lg sm:rounded-xl p-4 sm:p-5 hover:bg-purple-100 transition-all duration-300 hover:shadow-md cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <div className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-lg sm:rounded-xl bg-purple-100 group-hover:bg-purple-200 transition-colors">
                        <i className={`${committee.icon} text-xl sm:text-2xl text-purple-600 w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center`}></i>
                      </div>
                      <div>
                        <h4 className="font-bold text-purple-900 text-base sm:text-lg">{committee.name}</h4>
                        <p className="text-gray-600 font-light mt-0.5 sm:mt-1 text-sm">主委：{committee.chair}</p>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-purple-600 text-lg sm:text-xl opacity-0 group-hover:opacity-100 transition-opacity w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center"></i>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Sub-Clubs */}
          <div>
            <h3 className="text-2xl sm:text-3xl font-bold text-purple-900 mb-6 sm:mb-8 text-center sm:text-left mt-8 lg:mt-0">子社團</h3>
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {subClubs.map((club, index) => (
                <div 
                  key={index}
                  onClick={() => setSelectedClub(index)}
                  className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                >
                  <div className="w-10 sm:w-12 h-10 sm:h-12 flex items-center justify-center rounded-lg sm:rounded-xl bg-orange-50 mb-3 sm:mb-4 group-hover:bg-orange-100 transition-colors">
                    <i className={`${club.icon} text-xl sm:text-2xl text-orange-600 w-5 sm:w-6 h-5 sm:h-6 flex items-center justify-center`}></i>
                  </div>
                  <h4 className="font-bold text-gray-800 text-sm sm:text-base">{club.name}</h4>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Committee Detail Modal */}
        {selectedCommittee !== null && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setSelectedCommittee(null)}
          >
            <div
              className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl relative modal-slide-up max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle — mobile only */}
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-gray-200"></div>
              </div>

              <div className="p-5 sm:p-8">
                <button
                  onClick={() => setSelectedCommittee(null)}
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600 w-5 h-5 flex items-center justify-center"></i>
                </button>

                <div className="flex items-start gap-4 sm:gap-6 mb-5 sm:mb-6 pr-10">
                  <div className="w-12 sm:w-16 h-12 sm:h-16 flex items-center justify-center rounded-xl sm:rounded-2xl bg-purple-50 flex-shrink-0">
                    <i className={`${committees[selectedCommittee].icon} text-2xl sm:text-3xl text-purple-600 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl sm:text-3xl font-bold text-purple-900 mb-1 sm:mb-2">
                      {committees[selectedCommittee].name}
                    </h3>
                    <p className="text-orange-600 font-medium text-sm sm:text-base">
                      主委：{committees[selectedCommittee].chair}
                    </p>
                  </div>
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h4 className="text-sm sm:text-base font-bold text-gray-800 mb-2">委員會簡介</h4>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {committees[selectedCommittee].description}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3">主要職責</h4>
                    <div className="space-y-2">
                      {committees[selectedCommittee].responsibilities.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-500 flex-shrink-0"></div>
                          <p className="text-gray-600 text-sm sm:text-base">{item}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="pt-3 border-t border-gray-100 pb-safe">
                    <button
                      onClick={() => { setSelectedCommittee(null); window.open('https://lin.ee/pVaOePX', '_blank', 'noopener,noreferrer'); }}
                      className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-300 whitespace-nowrap text-sm sm:text-base cursor-pointer"
                    >
                      加入我們
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Club Detail Modal */}
        {selectedClub !== null && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-6"
            onClick={() => setSelectedClub(null)}
          >
            <div
              className="bg-white rounded-t-3xl sm:rounded-3xl w-full sm:max-w-2xl relative modal-slide-up max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle — mobile only */}
              <div className="flex justify-center pt-3 pb-1 sm:hidden">
                <div className="w-10 h-1 rounded-full bg-gray-200"></div>
              </div>

              <div className="p-5 sm:p-8">
                <button
                  onClick={() => setSelectedClub(null)}
                  className="absolute top-4 right-4 sm:top-6 sm:right-6 w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-close-line text-xl text-gray-600 w-5 h-5 flex items-center justify-center"></i>
                </button>

                <div className="flex items-start gap-4 sm:gap-6 mb-5 sm:mb-6 pr-10">
                  <div className="w-12 sm:w-16 h-12 sm:h-16 flex items-center justify-center rounded-xl sm:rounded-2xl bg-orange-50 flex-shrink-0">
                    <i className={`${subClubs[selectedClub].icon} text-2xl sm:text-3xl text-orange-600 w-6 sm:w-8 h-6 sm:h-8 flex items-center justify-center`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl sm:text-3xl font-bold text-purple-900 mb-1 sm:mb-2">
                      {subClubs[selectedClub].name}
                    </h3>
                  </div>
                </div>

                <div className="space-y-4 sm:space-y-6">
                  <div>
                    <h4 className="text-sm sm:text-base font-bold text-gray-800 mb-2">社團簡介</h4>
                    <p className="text-gray-600 leading-relaxed text-sm sm:text-base">
                      {subClubs[selectedClub].description}
                    </p>
                  </div>
                  <div>
                    <h4 className="text-sm sm:text-base font-bold text-gray-800 mb-2 sm:mb-3">主要活動</h4>
                    <div className="space-y-2">
                      {subClubs[selectedClub].activities.map((activity, idx) => (
                        <div key={idx} className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0"></div>
                          <p className="text-gray-600 text-sm sm:text-base">{activity}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="pt-3 border-t border-gray-100 pb-safe">
                    <button
                      onClick={() => { setSelectedClub(null); navigate('/contact'); }}
                      className="w-full py-3.5 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-xl font-semibold hover:opacity-90 transition-all duration-300 whitespace-nowrap text-sm sm:text-base cursor-pointer"
                    >
                      申請加入社團
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUpModal {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        @keyframes fadeInModal {
          from { opacity: 0; transform: scale(0.96); }
          to   { opacity: 1; transform: scale(1); }
        }
        .modal-slide-up {
          animation: slideUpModal 0.32s cubic-bezier(0.32, 0.72, 0, 1);
        }
        @media (min-width: 640px) {
          .modal-slide-up {
            animation: fadeInModal 0.25s ease-out;
          }
        }
        .pb-safe {
          padding-bottom: max(1rem, env(safe-area-inset-bottom));
        }
      `}</style>
    </section>
  );
}
